import React, { useEffect, useCallback } from 'react';
import { TextV2, Box } from '@withjoy/joykit';
import { StyledBoxMob, StyledCheckoutButton } from './ShoppingCart.styles';
import { ShoppingCartOpenButton } from './components';
import { useShoppingCartController } from './ShoppingCart.controller';

import { useTranslation } from '@shared/core';
import { ReactComponent as CartIcon } from '@assets/icons/cart-icon.svg';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { ShoppingCartDialog } from './ShoppingCartDialog';
import type { RegistryOrderList, RegistryList } from '@apps/registry/common/state/RegistryProducts';
import type { ShippingAddressFragment } from '@graphql/aliases';
import { withWindow } from '@shared/utils/withWindow';
import { useHistory } from '@react-router';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useShoppingCart } from '@apps/registry/common/components/ShoppingCart/state';
import { useMount } from '@shared/utils/hooks/useMount';
import { useShoppingCartTelemetry } from './ShoppingCart.telemetry';
import { getCartDeepLinkSection } from './utils/cartDeepLink';

export type { ProductList } from './ShoppingCartInner';
export { ShoppingCartInner } from './ShoppingCartInner';

interface ShoppingCartProps
  extends Readonly<{
    eventId: string;
    isShoppingPage?: boolean;
    orders: RegistryOrderList;
    registry: RegistryList;
    shippingAddress: ShippingAddressFragment | null;
    registryCurrencyCode?: string;
  }> {}

export const ShoppingCart: React.FC<ShoppingCartProps> = ({ eventId, isShoppingPage, registry, shippingAddress, orders, registryCurrencyCode }) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const { t } = useTranslation('sharedRegistry');
  const { viewGift } = t('shoppingCart');

  const paths = useRoutePaths();
  const {
    state: { isShoppingCartOpen },
    mutators: { openShoppingCart, closeShoppingCart },
  } = useShoppingCart();

  const history = useHistory();
  const previousUrl = history.location.pathname;

  const { helpDialogOpen, productList, reservedOrderList, purchasedOrderList, orderCount, cartOrderCount, cartTotal, showHelpDialog, handleDialogClose } =
    useShoppingCartController({
      registries: registry,
      orders,
      registryCurrencyCode,
    });

  const { shoppingCartButtonClick } = useShoppingCartTelemetry();

  const showShoppingCartDialog = useCallback(() => {
    shoppingCartButtonClick({ eventId, device: isMobileOrTablet ? 'mobileOrTablet' : 'desktop' });
    withWindow(window => window.history.pushState(null, 'product', window.location.href));
    openShoppingCart();
  }, [isMobileOrTablet, openShoppingCart, eventId, shoppingCartButtonClick]);

  useMount(() => {
    withWindow(({ location }) => {
      if (getCartDeepLinkSection(location.search)) {
        showShoppingCartDialog();
      }
    });
  });

  useEffect(() => {
    return history.listen(() => {
      if (history.action === 'POP') {
        withWindow(window => window.history.replaceState(null, 'product', previousUrl));
        closeShoppingCart();
      }
    });
  }, [history, closeShoppingCart, previousUrl]);

  return (
    <>
      <ShoppingCartDialog
        isOpen={isShoppingCartOpen}
        eventId={eventId}
        shippingAddress={shippingAddress}
        isShoppingPage={isShoppingPage}
        helpDialogOpen={helpDialogOpen}
        productList={productList}
        reservedOrderList={reservedOrderList}
        purchasedOrderList={purchasedOrderList}
        orderCount={orderCount}
        cartOrderCount={cartOrderCount}
        cartTotal={cartTotal}
        paths={paths}
        onClose={closeShoppingCart}
        showHelpDialog={showHelpDialog}
        handleDialogClose={handleDialogClose}
      />
      {isMobileOrTablet && !isShoppingPage ? (
        <StyledBoxMob>
          <StyledCheckoutButton
            intent="primary"
            variant="solid"
            onClick={showShoppingCartDialog}
          >
            <Box
              alignItems="center"
              display="inline-flex"
            >
              <Box display="flex">
                <CartIcon />
                {orderCount > 0 && (
                  <TextV2
                    typographyVariant="hed2"
                    color={'white'}
                  >
                    {orderCount}
                  </TextV2>
                )}
              </Box>
              <Box display="flex">
                <TextV2
                  typographyVariant="hed2"
                  color={'white'}
                  marginLeft={6}
                >
                  {viewGift()}
                </TextV2>
              </Box>
            </Box>
          </StyledCheckoutButton>
        </StyledBoxMob>
      ) : (
        <ShoppingCartOpenButton
          onClick={showShoppingCartDialog}
          orderCount={orderCount}
        />
      )}
    </>
  );
};
